import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  Box,
  Input,
  List,
  ListItem,
  Text,
  useColorModeValue,
  useOutsideClick,
} from '@chakra-ui/react';

const SearchableCombobox = ({
  options,
  value,
  onChange,
  name,
  placeholder = 'Select an option',
  emptyStateText = 'No options available',
  noResultsText = 'No matching results',
  isDisabled = false,
  isInvalid = false,
}) => {
  const containerRef = useRef(null);
  const listId = useId();
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const dropdownBg = useColorModeValue('white', 'gray.800');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const activeBg = useColorModeValue('blue.50', 'blue.700');

  const sortedOptions = useMemo(
    () => [...options].sort((a, b) => (a?.label || '').localeCompare(b?.label || '')),
    [options]
  );

  const selectedOption = useMemo(
    () => sortedOptions.find((option) => String(option.value) === String(value)) || null,
    [sortedOptions, value]
  );

  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(selectedOption?.label || '');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const filteredOptions = useMemo(() => {
    const query = inputValue.trim().toLocaleLowerCase();
    if (!query) return sortedOptions;
    return sortedOptions.filter((option) => (option?.label || '').toLocaleLowerCase().includes(query));
  }, [inputValue, sortedOptions]);

  useEffect(() => {
    setInputValue(selectedOption?.label || '');
  }, [selectedOption]);

  useEffect(() => {
    if (!isOpen) {
      setHighlightedIndex(-1);
      return;
    }
    setHighlightedIndex(filteredOptions.length > 0 ? 0 : -1);
  }, [filteredOptions, isOpen]);

  useOutsideClick({
    ref: containerRef,
    handler: () => {
      setIsOpen(false);
      setInputValue(selectedOption?.label || '');
    },
  });

  const emitChange = (nextValue) => {
    if (!onChange) return;
    if (name) {
      onChange({ target: { name, value: nextValue } });
      return;
    }
    onChange(nextValue);
  };

  const handleSelect = (option) => {
    setInputValue(option.label);
    setIsOpen(false);
    setHighlightedIndex(-1);
    emitChange(option.value);
  };

  const handleInputChange = (event) => {
    const nextValue = event.target.value;
    setInputValue(nextValue);
    setIsOpen(true);

    if (!selectedOption || nextValue !== selectedOption.label) {
      emitChange('');
    }
  };

  const handleKeyDown = (event) => {
    if (isDisabled) return;

    if (!isOpen && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      setIsOpen(true);
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (filteredOptions.length > 0) {
        setHighlightedIndex((prev) => (prev + 1) % filteredOptions.length);
      }
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (filteredOptions.length > 0) {
        setHighlightedIndex((prev) => (prev <= 0 ? filteredOptions.length - 1 : prev - 1));
      }
      return;
    }

    if (event.key === 'Enter' && isOpen && highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
      event.preventDefault();
      handleSelect(filteredOptions[highlightedIndex]);
      return;
    }

    if (event.key === 'Escape') {
      setIsOpen(false);
      setInputValue(selectedOption?.label || '');
    }
  };

  const showEmptyState = sortedOptions.length === 0;
  const showNoResults = !showEmptyState && filteredOptions.length === 0;

  return (
    <Box ref={containerRef} position="relative">
      <Input
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-controls={listId}
        aria-activedescendant={
          highlightedIndex >= 0 && filteredOptions[highlightedIndex]
            ? `${listId}-option-${filteredOptions[highlightedIndex].value}`
            : undefined
        }
        aria-invalid={isInvalid ? 'true' : undefined}
        name={name}
        value={inputValue}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        autoComplete="off"
        onFocus={() => !isDisabled && setIsOpen(true)}
        onClick={() => !isDisabled && setIsOpen(true)}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
      />

      {isOpen && !isDisabled ? (
        <Box
          position="absolute"
          top="calc(100% + 4px)"
          width="100%"
          bg={dropdownBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="md"
          boxShadow="md"
          zIndex={10}
          maxH="240px"
          overflowY="auto"
        >
          {showEmptyState ? (
            <Text px={3} py={2} color="gray.500" fontSize="sm">
              {emptyStateText}
            </Text>
          ) : showNoResults ? (
            <Text px={3} py={2} color="gray.500" fontSize="sm">
              {noResultsText}
            </Text>
          ) : (
            <List id={listId} role="listbox" m={0} p={1} styleType="none">
              {filteredOptions.map((option, index) => {
                const isHighlighted = index === highlightedIndex;
                const isSelected = String(option.value) === String(value);

                return (
                  <ListItem
                    key={option.value}
                    id={`${listId}-option-${option.value}`}
                    role="option"
                    aria-selected={isSelected}
                    px={3}
                    py={2}
                    borderRadius="md"
                    cursor="pointer"
                    bg={isHighlighted ? activeBg : 'transparent'}
                    _hover={{ bg: isHighlighted ? activeBg : hoverBg }}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      handleSelect(option);
                    }}
                  >
                    {option.label}
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>
      ) : null}
    </Box>
  );
};

export default SearchableCombobox;
